import { AccountInterface, Call, ProviderInterface, Contract } from 'starknet';
import { Decimal, DEFAULT_DECIMAL_PRECISION, Uint256 } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { executeTransaction } from '../../utils';
import LentDebtToken_ABI from './abi/LentDebtToken_abi.json';

export class LentDebtTokenContract {
  private contract: Contract;
  private precision: number;

  constructor(precision: number, address: string, providerOrAccount: ProviderInterface | AccountInterface) {
    this.contract = new Contract({ abi: LentDebtToken_ABI, address, providerOrAccount });

    this.precision = precision;
  }

  async balanceOf(address: string): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const balance = await this.contract.balanceOf(address);

    return new Decimal(balance[0] as Uint256, this.precision);
  }

  async totalSupply(): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const supply = await this.contract.totalSupply();

    return new Decimal(supply[0], this.precision);
  }

  async getTotalSupplyCap(): Promise<Decimal | null> {
    if (!this.contract) {
      return null;
    }

    const supply = await this.contract.getTotalSupplyCap();

    return new Decimal(supply[0] as Uint256, this.precision);
  }

  async mint(amount: Decimal, to: string, account: AccountInterface): Promise<InvokeResponse | null> {
    if (!this.contract) {
      return null;
    }

    const amountUint256 = amount.toUint256(this.precision);

    const calls = [
      {
        contractAddress: this.contract.address,
        entrypoint: 'mint',
        calldata: [to, amountUint256.low, amountUint256.high],
      },
    ] as Call[];

    return executeTransaction(account, calls);
  }

  async burn(
    asset: Asset,
    amount: Decimal,
    inputAmount: Decimal,
    burnFrom: string,
    account: AccountInterface,
  ): Promise<InvokeResponse | null> {
    if (!this.contract) {
      return null;
    }

    const approvalAmount = amount.equals(inputAmount) ? amount : inputAmount.add(inputAmount.div(100));
    const approvalAmountUint256 = approvalAmount.toUint256(asset.tokenPrecision);

    // If amount is Decimal.MAX_DECIMAL we don't want to reduce number of decimals to match token decimals, we want to keep amount to 18 decimals.
    // This way contract will properly recognize passed amount as Decimal.MAX_DECIMAL value.
    const amountUint256 = amount.equals(inputAmount)
      ? amount.toUint256(asset.tokenPrecision)
      : amount.toUint256(DEFAULT_DECIMAL_PRECISION);

    const calls = [
      {
        contractAddress: asset.address,
        entrypoint: 'approve',
        calldata: [this.contract.address, approvalAmountUint256.low, approvalAmountUint256.high],
      },
      {
        contractAddress: this.contract.address,
        entrypoint: 'burn',
        calldata: [burnFrom, amountUint256.low, amountUint256.high],
      },
    ] as Call[];

    return executeTransaction(account, calls);
  }
}
